import React, { useEffect, useState } from 'react'
import { FormLayout } from 'styles/FormLayout'
import { authenticatedGet, authenticatedPost } from 'lib/authenticatedGet'
import { API_HOST } from 'lib'
import { LoadingSpinner } from '../../LoadingSpinner'
import { ToleranceGroupDTO, ToleranceGroupSummaryDTO } from '../../../domain/DTO'
import { ToleranceGroupRow } from './ToleranceGroupRow'
import { EmptyToleranceGroupRow } from './EmptyToleranceGroupRow'
import { AddCircleIcon } from '../../../icons'

type ToleranceGroupSnapshot = {
  commonId: string
  hasChanges: boolean
  toleranceGroups: ToleranceGroupDTO[]
}

type Props = {
  commonId: string
  onApply: (id: string, snapshot: ToleranceGroupSnapshot) => void
}

type ToleranceFieldConfig = {
  label: string
  field: keyof ToleranceGroupDTO
  placeholder: string
}

const toleranceFields: ToleranceFieldConfig[] = [
  { label: 'ToleranceGroupId', field: 'toleranceGroupId', placeholder: '' },
  { label: 'Height-', field: 'minHeight', placeholder: '' },
  { label: 'Height+', field: 'maxHeight', placeholder: '' },
  { label: 'Diameter-', field: 'minDiameter', placeholder: '' },
  { label: 'Diameter+', field: 'maxDiameter', placeholder: '' },
]

export const ToleranceGroupsDialog = ({ commonId, onApply }: Props) => {
  const [toleranceGroups, setToleranceGroups] = useState<ToleranceGroupDTO[]>([])
  const [newToleranceGroups, setNewToleranceGroups] = useState<ToleranceGroupDTO[]>([])
  const [originalToleranceGroups, setOriginalToleranceGroups] = useState<ToleranceGroupSummaryDTO[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const updateNewToleranceGroup = (index: number, field: keyof ToleranceGroupDTO, value: number | null) => {
    setNewToleranceGroups((prevNewGroups) => {
      if (index < 0 || index >= prevNewGroups.length) return prevNewGroups
      return prevNewGroups.map((toleranceGroupDTO, i) =>
        i === index ? { ...toleranceGroupDTO, [field]: value } : toleranceGroupDTO,
      )
    })
  }

  const updateExistingToleranceGroup = (
    field: keyof ToleranceGroupDTO,
    value: number | null,
    toleranceGroupId: number,
  ) => {
    setToleranceGroups((prevGroups) => {
      const toleranceGroupExists = prevGroups.some((group) => group.toleranceGroupId === toleranceGroupId)

      if (toleranceGroupExists) {
        return prevGroups.map((toleranceGroupDTO) => {
          if (toleranceGroupDTO.toleranceGroupId === toleranceGroupId) {
            return {
              ...toleranceGroupDTO,
              [field]: value,
            }
          }
          return toleranceGroupDTO
        })
      } else {
        const newToleranceGroup: ToleranceGroupDTO = {
          commonId,
          toleranceGroupId,
          minHeight: null,
          maxHeight: null,
          minDiameter: null,
          maxDiameter: null,
        }
        return [...prevGroups, { ...newToleranceGroup, [field]: value }]
      }
    })
  }

  const handleDeleteNewRow = (index: number) => {
    setNewToleranceGroups((prev) => prev.filter((_, i) => i !== index))
  }

  const handleAddRow = () => {
    const newRow = {
      commonId,
      toleranceGroupId: null,
      minHeight: null,
      maxHeight: null,
      minDiameter: null,
      maxDiameter: null,
    }
    setNewToleranceGroups([...newToleranceGroups, newRow])
  }

  const checkHasChanges = () => {
    const originalFlattened = originalToleranceGroups.flatMap((summary) => summary.toleranceGroupsFromDb)
    return JSON.stringify(toleranceGroups) !== JSON.stringify(originalFlattened)
  }

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null)
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [error])

  useEffect(() => {
    const fetchToleranceGroups = async () => {
      try {
        setLoading(true)
        setError(null)
        const data = await authenticatedGet<ToleranceGroupSummaryDTO[]>(
          `${API_HOST}/bff/common/${commonId}/tolerance-groups`,
        )
        if (data && data.length > 0) {
          const result = JSON.parse(JSON.stringify(data))
          setOriginalToleranceGroups(result)
          const allToleranceGroupsFromDb = result.flatMap(
            (summary: ToleranceGroupSummaryDTO) => summary.toleranceGroupsFromDb,
          )
          setToleranceGroups(allToleranceGroupsFromDb)
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load tolerance groups')
      } finally {
        setLoading(false)
      }
    }
    void fetchToleranceGroups()
  }, [commonId])

  const handleSave = async () => {
    const hasNullGroupId = toleranceGroups.some(
      (group) => group.toleranceGroupId === null || group.toleranceGroupId === undefined,
    )

    const hasIncompleteNewRow = newToleranceGroups.some(
      (group) => group.toleranceGroupId === null || group.toleranceGroupId === undefined,
    )

    if (hasNullGroupId || hasIncompleteNewRow) {
      setError('ToleranceGroupId cannot be empty!')
      setToleranceGroups((prevGroups) => prevGroups.filter((group) => group.toleranceGroupId !== null))
      return
    }

    const existingGroupIds = new Set(
      originalToleranceGroups.flatMap((toleranceGroupSummaryDTO) =>
        (
          toleranceGroupSummaryDTO.toleranceGroupsFromCpg.concat(toleranceGroupSummaryDTO.toleranceGroupsFromDb) ?? []
        ).map((toleranceGroupDTO) => toleranceGroupDTO.toleranceGroupId),
      ),
    )
    const duplicateNewGroup = newToleranceGroups.find(
      (group) => group.toleranceGroupId !== null && existingGroupIds.has(group.toleranceGroupId),
    )
    if (duplicateNewGroup) {
      setError(`ToleranceGroupId ${duplicateNewGroup.toleranceGroupId} already exists!`)
      return
    }

    const newToleranceGroupIds = new Set<number>()
    const duplicateAmongNew = newToleranceGroups.find((toleranceGroupDTO) => {
      if (toleranceGroupDTO.toleranceGroupId !== null) {
        if (newToleranceGroupIds.has(toleranceGroupDTO.toleranceGroupId)) return true
        newToleranceGroupIds.add(toleranceGroupDTO.toleranceGroupId)
      }
      return false
    })
    if (duplicateAmongNew) {
      setError(`ToleranceGroupId ${duplicateAmongNew.toleranceGroupId} already exists!`)
      return
    }

    try {
      setSaving(true)
      setError(null)
      const allGroups = [...toleranceGroups, ...newToleranceGroups]
      await authenticatedPost<void>(
        `${API_HOST}/bff/common/${commonId}/tolerance-groups`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(allGroups),
        },
        120000,
      )
      onApply(commonId, { commonId, hasChanges: checkHasChanges(), toleranceGroups: allGroups })
    } catch (error) {
      let errorMessage = 'Failed to save tolerance groups'

      if (error && typeof error === 'object') {
        const errorObject = error as Record<string, unknown>

        if (errorObject.body && typeof errorObject.body === 'object') {
          const body = errorObject.body as Record<string, unknown>
          if (body.detail) {
            errorMessage = String(body.detail)
          } else if (body.message) {
            errorMessage = String(body.message)
          }
        }
      }
      setError(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="card">
      <FormLayout.Content className="overflow-y-auto max-h-screen-md">
        <h3 className="text-lg mb-md">Set/override tolerance groups</h3>

        {loading && <LoadingSpinner />}

        {!loading && (
          <>
            {error && (
              <div className="alert alert-danger fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50">
                {error}
              </div>
            )}
            <div className="flex items-end gap-1">
              {toleranceFields.map((fieldConfig) => (
                <label key={fieldConfig.field} className="inline-flex flex-col w-10">
                  {fieldConfig.label}
                </label>
              ))}
            </div>
            <div className="space-y-1">
              {originalToleranceGroups.map((toleranceGroupSummaryDTO, index) => (
                <ToleranceGroupRow
                  key={index}
                  index={index}
                  commonId={commonId}
                  toleranceGroupSummaryDTO={toleranceGroupSummaryDTO}
                  toleranceFields={toleranceFields}
                  onFieldChange={updateExistingToleranceGroup}
                  onDelete={(index) => {
                    const deletedToleranceGroup = originalToleranceGroups[index]
                    if (deletedToleranceGroup && deletedToleranceGroup.toleranceGroupsFromDb) {
                      const deletedGroupIds = deletedToleranceGroup.toleranceGroupsFromDb.map(
                        (toleranceGroupDTO) => toleranceGroupDTO.toleranceGroupId,
                      )
                      setToleranceGroups((prevToleranceGroups) =>
                        prevToleranceGroups.filter(
                          (toleranceGroupDTO) => !deletedGroupIds.includes(toleranceGroupDTO.toleranceGroupId),
                        ),
                      )
                    }
                    setOriginalToleranceGroups(originalToleranceGroups.filter((_, i) => i !== index))
                  }}
                />
              ))}
            </div>
          </>
        )}

        <div>
          {newToleranceGroups.map((row, i) => (
            <EmptyToleranceGroupRow
              key={`newToleranceGroupRow-${i}`}
              index={i}
              toleranceGroupDTO={row}
              toleranceFields={toleranceFields}
              onFieldChange={updateNewToleranceGroup}
              onDelete={handleDeleteNewRow}
            />
          ))}
          <button type="button" onClick={handleAddRow} className="mt-1">
            <AddCircleIcon color="none" />
          </button>
        </div>

        <FormLayout.FooterContainer>
          <FormLayout.Footer>
            <button className="btn btn-primary-dark py-sm" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </FormLayout.Footer>
        </FormLayout.FooterContainer>
      </FormLayout.Content>
    </div>
  )
}
